import React from "react";
import styled, { keyframes } from "styled-components";

// CRA serves public/ at the site root in dev and production.
const headshotSrc = `${process.env.PUBLIC_URL}/images/headshot.jpg`;

const AboutMeIntro = () => {
  return (
    <Section aria-labelledby="about-me-heading">
      <Panel>
        <Portrait>
          <Headshot
            src={headshotSrc}
            alt="James R, founder of StudioVoice2Fly"
          />
        </Portrait>
        <Identity>
          <Kicker>About</Kicker>
          <Heading id="about-me-heading">James R</Heading>
        </Identity>
        <Details>
          <Bio>
            I help founders and organizations turn complex ideas into practical
            AI-powered products, systems, and strategies. Work combines AI,
            product development, psychology, automation, UX, and systems
            thinking. Human-first means the tool stays simple and the person
            stays in charge.
          </Bio>
          <Tagline>Practical AI, built human-first.</Tagline>
        </Details>
      </Panel>
    </Section>
  );
};

export default AboutMeIntro;

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Section = styled.section`
  position: relative;
  z-index: 3;
  width: 100%;
  padding: 96px 24px 8px;
  background: transparent;

  @media (max-width: 768px) {
    padding: 88px 16px 4px;
  }
`;

const Panel = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  grid-template-areas:
    "photo identity"
    "photo details";
  align-items: center;
  column-gap: 40px;
  row-gap: 0;
  padding: 36px 40px;
  background: linear-gradient(
    135deg,
    rgba(15, 15, 15, 0.9),
    rgba(30, 30, 30, 0.8)
  );
  border: 1px solid #45fe47;
  border-radius: 12px;
  color: #fff;
  animation: ${fadeUp} 0.7s ease 0.15s both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (max-width: 768px) {
    grid-template-columns: 96px minmax(0, 1fr);
    grid-template-areas:
      "photo identity"
      "details details";
    column-gap: 14px;
    row-gap: 16px;
    padding: 18px 18px 20px;
  }
`;

const Portrait = styled.figure`
  grid-area: photo;
  margin: 0;
`;

const Headshot = styled.img`
  display: block;
  width: 148px;
  height: 148px;
  border-radius: 50%;
  object-fit: cover;
  object-position: center 18%;
  border: 2px solid #45fe47;

  @media (max-width: 768px) {
    width: 96px;
    height: 96px;
  }
`;

const Identity = styled.div`
  grid-area: identity;
  max-width: 680px;
`;

const Details = styled.div`
  grid-area: details;
  max-width: 680px;
`;

const Kicker = styled.p`
  font-family: "VT323", monospace;
  font-size: 16px;
  line-height: 16px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: #45fe47;
  margin: 0 0 12px;

  @media (max-width: 768px) {
    margin-bottom: 4px;
  }
`;

const Heading = styled.h2`
  font-family: "VT323", monospace;
  font-size: 40px;
  line-height: 1.1;
  font-weight: 600;
  color: #f7f8fa;
  margin: 0 0 16px;

  @media (max-width: 768px) {
    font-size: 32px;
    margin-bottom: 0;
  }
`;

const Bio = styled.p`
  font-family: "VT323", monospace;
  font-size: 22px;
  line-height: 1.35;
  color: #f7f8fa;
  margin: 0 0 14px;

  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 12px;
  }
`;

const Tagline = styled.p`
  font-family: "VT323", monospace;
  font-size: 18px;
  line-height: 1.3;
  letter-spacing: 0.6px;
  color: #45fe47;
  margin: 0;
`;
